import { FestivalData, NewsItem, Page } from '../model/FestivalData';
import { User } from '../model/User';
import { Carpool } from '../model/Carpool';
import { CarpoolReport } from '../model/Report';
import { Advertisement } from '../model/Advertisement';
import { Language } from '../i18n/language';
import { currentMessages } from '../i18n/I18n';
import { FestivalExcerpt } from '../utils/festivals';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Our own error messages, in the language shown when the error happens; the server's messages stay as they are
const t = () => currentMessages('api');


const isFestivalId = (festivalId: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(festivalId);

/**
 * What went wrong when signing in or asking for a reset code, in words: the API answers these with codes
 * ("UNAUTHORIZED") or generic texts ("Wrong request body"). `otherwise` covers what's left for that request.
 */
const authError = (status: number, serverMessage: string | undefined, otherwise: string): string => {
  if (status === 429) return t().tooManyAttempts;
  if (serverMessage === "Festival doesn't exists") return t().unknownFestival;
  return otherwise;
};

/** A wrong master key gets "UNAUTHORIZED", or "Missing X-Session-Token header" where a session would also do */
const isWrongMasterKey = (status: number, serverMessage: string | undefined) =>
  status === 401 || !!serverMessage?.startsWith('Missing X-Session-Token');

interface LoginResponse {
  token: string;
  user: User;
}

export const login = async (email: string, password: string, festivalId: string): Promise<LoginResponse> => {
  if (!isFestivalId(festivalId)) throw new Error(t().invalidFestivalId);
  const response = await fetch(`${API_BASE_URL}/api/v1/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    // An unknown email gets the same answer as a wrong password
    const wrongCredentials = response.status === 401 || response.status === 404;
    throw new Error(authError(response.status, data.message, wrongCredentials ? t().wrongCredentials : data.message || t().loginFailed));
  }

  return {
    token: data.data.session.token,
    user: data.data.user
  };
};

export const logout = async (sessionToken: string, festivalId: string): Promise<void> => {
  await fetch(`${API_BASE_URL}/api/v1/logout`, {
    method: 'POST',
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    },
  });
};

export const fetchVersions = async (sessionToken: string, festivalId: string): Promise<FestivalData[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/festival/versions`, {
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || t().fetchVersionsFailed);
  }

  return data.data.versions;
};

export const updateFestivalData = async (
  sessionToken: string, 
  festivalId: string, 
  versionId: string, 
  data: FestivalData
): Promise<{ updated_at: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/festival/update`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    },
    body: JSON.stringify({ festival_data: data }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().updateFestivalDataFailed);
  }

  const responseData = await response.json();
  return { updated_at: responseData.data.updated_at };
};

export const uploadImage = async (file: File, sessionToken: string, festivalId: string): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/v1/resources`, {
    method: 'POST',
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().uploadImageFailed);
  }

  const data = await response.json();
  return data.data.file_identifier;
};

export const imageUrl = (identifier: string, festivalId: string) => {
  return `${API_BASE_URL}/api/v1/resources/${identifier}?festival_id=${festivalId}`;
};

export const fetchCurrentVersion = async (
  sessionToken: string,
  festivalId: string,
  versionId: string
): Promise<FestivalData> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/festival/versions`, {
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().fetchCurrentVersionFailed);
  }

  const data = await response.json();
  const currentVersion = data.data.versions.find(
    (version: FestivalData) => version.version.toString() === versionId
  );

  if (!currentVersion) {
    throw new Error(t().versionNotFound);
  }

  return currentVersion;
};

export const createUserUserMasterKey = async (
  masterKey: string, 
  festivalId: string, 
  email: string, 
  roles: string[]
) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Master-Key': masterKey,
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify({
      email,
      roles,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(isWrongMasterKey(response.status, data.message) ? t().wrongMasterKey : t().createUserFailed(data.message));
  }

  return data;
};

export const createUser = async (
  sessionToken: string,
  festivalId: string, 
  email: string, 
  roles: string[]
) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify({
      email,
      roles,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(t().createUserFailed(data.message));
  }

  return data;
};

export const createFestival = async (
  masterKey: string,
  festivalName: string,
  adminEmail: string,
  firebaseSecretKey: object,
  subdomain: string
): Promise<{ festival_id: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/festival/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Master-Key': masterKey,
    },
    body: JSON.stringify({
      festival_name: festivalName,
      admin_email: adminEmail,
      firebase_secret_key: firebaseSecretKey,
      subdomain: subdomain,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(isWrongMasterKey(response.status, data.message) ? t().wrongMasterKey : data.message || t().createFestivalFailed);
  }

  return { festival_id: data.data.festival_id };
};

export const updateFirebaseSecret = async (
  masterKey: string,
  festivalId: string,
  firebaseSecretKey: object
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/festival/firebase-secret`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Master-Key': masterKey,
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify({
      firebase_secret_key: firebaseSecretKey,
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(isWrongMasterKey(response.status, data.message) ? t().wrongMasterKey : data.message || t().updateFirebaseSecretFailed);
  }
};

export interface RevisionExcerpt {
  uuid: string;
  user_email: string;
  date: string;
  version: number;
}

export const fetchRevisionsExcerpt = async (
  sessionToken: string,
  festivalId: string,
  versionId: string
): Promise<RevisionExcerpt[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/revisions/${versionId}`, {
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().fetchRevisionsFailed);
  }

  const data = await response.json();
  return data.data.revisions;
};

export const revertToRevision = async (
  sessionToken: string,
  festivalId: string,
  versionId: string,
  revisionUuid: string
): Promise<FestivalData> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/revisions/revert/${versionId}/${revisionUuid}`, {
    method: 'POST',
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().revertFailed);
  }

  const data = await response.json();
  return data.data;
};

export const sendNotification = async (
  sessionToken: string,
  festivalId: string,
  notification: {
    topic: string;
    title: string;
    body: string;
  }
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/notification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    },
    body: JSON.stringify(notification),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().sendNotificationFailed);
  }
};

export const fetchFestivals = async (masterKey: string): Promise<FestivalExcerpt[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/festivals`, {
    headers: {
      'X-Master-Key': masterKey,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(isWrongMasterKey(response.status, errorData.message) ? t().wrongMasterKey : errorData.message || t().fetchFestivalsFailed);
  }

  const data = await response.json();
  return data.data.festivals;
};

// Add new interface and API call for fetching full revision details
export interface Revision extends RevisionExcerpt {
  diff: string;  // Full diff content
  // The version's data just before and just after this revision, null when its revisions can't be replayed
  before: FestivalData | null;
  after: FestivalData | null;
}

export const fetchRevision = async (
  sessionToken: string,
  festivalId: string,
  revisionId: string
): Promise<Revision> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/revision/${revisionId}`, {
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().fetchRevisionFailed);
  }

  const data = await response.json();
  return data.data.revision;
};

export const fetchUsers = async (
  sessionToken: string,
  festivalId: string
): Promise<User[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/users`, {
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(t().fetchUsersFailed(data.message));
  }

  return data.data.users;
};

export const updateUserRoles = async (
  sessionToken: string,
  festivalId: string,
  userId: string,
  roles: string[]
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/user/${userId}/roles`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify({ roles }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(t().updateRolesFailed(data.message));
  }
};

// Add this new function to fetch current user data
export const fetchCurrentUser = async (
  sessionToken: string,
  festivalId: string
): Promise<User> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/current_user`, {
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(t().fetchUserFailed(data.message));
  }

  return data.data.user;
};

/** Saves the backoffice language on the signed-in user's account */
export const updateCurrentUserLanguage = async (
  sessionToken: string,
  festivalId: string,
  language: Language
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/current_user/language`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify({ language }),
  });

  if (!response.ok) {
    throw new Error(t().saveLanguageFailed(response.status));
  }
};

export const deleteUser = async (
  sessionToken: string,
  festivalId: string,
  userId: string
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/user/${userId}`, {
    method: 'DELETE',
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(t().deleteUserFailed(data.message));
  }
};

export const resetUserPassword = async (
  sessionToken: string,
  festivalId: string,
  userId: string
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/user/${userId}/reset_password`, {
    method: 'POST',
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || t().resetPasswordFailed);
  }
};

export const generateRecoverPasswordCode = async (email: string, festivalId: string): Promise<void> => {
  if (!isFestivalId(festivalId)) throw new Error(t().invalidFestivalId);
  const response = await fetch(`${API_BASE_URL}/api/v1/user/generate_recover_password_code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok) {
    // The API reports an unknown email as a bad request
    throw new Error(authError(response.status, data.message, response.status === 400 ? t().unknownAccountEmail : data.message || t().recoveryCodeFailed));
  }
};

export const updatePassword = async (
  email: string,
  otp: string,
  password: string,
  festivalId: string
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/user/update_password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify({ email, otp, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || t().updatePasswordFailed);
  }
};

export const performMigrations = async (masterKey: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/migrate`, {
    method: 'GET',
    headers: {
      'X-Master-Key': masterKey,
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(isWrongMasterKey(response.status, data.message) ? t().wrongMasterKey : data.message || t().migrationsFailed);
  }
};

export const getFestivalIdFromSubdomain = async (subdomain: string): Promise<string> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/festival/subdomain/${subdomain}`);
  if (!response.ok) {
    throw new Error(t().festivalIdFailed);
  }
  const data = await response.json();
  return data.data.festival_id;
};

export interface CarpoolsResponse {
  carpools: Carpool[];
}

export const fetchCarpools = async (
  sessionToken: string,
  festivalId: string
): Promise<Carpool[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/carpools`, {
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().fetchCarpoolsFailed);
  }

  const data = await response.json();
  return data.data.carpools;
};

export const deleteCarpool = async (
  sessionToken: string,
  festivalId: string,
  carpoolId: string
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/carpool/${carpoolId}`, {
    method: 'DELETE',
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().deleteCarpoolFailed);
  }
};

export const fetchReports = async (
  sessionToken: string,
  festivalId: string
): Promise<CarpoolReport[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/reports`, {
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().fetchReportsFailed);
  }

  const data = await response.json();
  return data.data.carpool_reports;
};

export const ignoreReport = async (
  sessionToken: string,
  festivalId: string,
  reportId: string
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/report/${reportId}/ignore`, {
    method: 'POST',
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().ignoreReportFailed);
  }
};

export const applyReport = async (
  sessionToken: string,
  festivalId: string,
  reportId: string
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/report/${reportId}/apply`, {
    method: 'POST',
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().applyReportFailed);
  }
};

// Add these new functions
export const fetchNews = async (
  sessionToken: string,
  festivalId: string
): Promise<NewsItem[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/news`, {
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().fetchNewsFailed);
  }

  const data = await response.json();
  return data.data.news;
};

export const updateNews = async (
  sessionToken: string,
  festivalId: string,
  newsItem: NewsItem,
  notify_users_on_create?: boolean
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/news`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    },
    body: JSON.stringify({ 
      news: newsItem,
      notify_users_on_create: notify_users_on_create || false
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().updateNewsFailed);
  }
};

export const deleteNewsItem = async (
  sessionToken: string,
  festivalId: string,
  newsId: string
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/news/${newsId}`, {
    method: 'DELETE',
    headers: {
      'X-Festival-Id': festivalId,
      'X-Session-Token': sessionToken,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().deleteNewsFailed);
  }
};

export const fetchPages = async (sessionToken: string, festivalId: string): Promise<Page[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/pages`, {
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().fetchPagesFailed);
  }
  const data = await response.json();
  return data.data.pages;
};

export const updatePage = async (sessionToken: string, festivalId: string, page: Page): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/pages`, {
    method: 'PUT',
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ page }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().updatePageFailed);
  }
};

export const deletePage = async (sessionToken: string, festivalId: string, pageId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/page/${pageId}`, {
    method: 'DELETE',
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().deletePageFailed);
  }
};

export const fetchAdvertisements = async (
  sessionToken: string,
  festivalId: string
): Promise<Advertisement[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/advertisements`, {
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().fetchAdsFailed);
  }

  const data = await response.json();
  console.log('Fetched advertisements:', data);
  return data.data.advertisements;
};

export const updateAdvertisement = async (
  sessionToken: string,
  festivalId: string,
  advertisement: any
) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/advertisements`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify({ advertisement }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().updateAdFailed);
  }
};

export const deleteAdvertisement = async (
  sessionToken: string,
  festivalId: string,
  adId: string
) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/advertisements/${adId}`, {
    method: 'DELETE',
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().deleteAdFailed);
  }
};

// Add these interfaces
export interface CustomNotificationTopic {
  identifier: string;
  name: string;
}

// Add these functions
export const getTopics = async (
  sessionToken: string,
  festivalId: string
): Promise<CustomNotificationTopic[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/topics`, {
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
  });
  if (!response.ok) throw new Error(t().fetchTopicsFailed);
  const data = await response.json();
  return data.data.topics;
};

export const upsertTopic = async (
  sessionToken: string,
  festivalId: string,
  topic: CustomNotificationTopic
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/topics`, {
    method: 'POST',
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ custom_notification_topic: topic }),
  });
  if (!response.ok) throw new Error(t().saveTopicFailed);
};

export const getSubdomainFromFestivalId = async (festivalId: string): Promise<string> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/festival/subdomain/festival/${festivalId}`);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || t().subdomainFailed);
  }

  return data.data.subdomain;
};

export type ScheduledNotificationStatus = 'pending' | 'sent' | 'cancelled' | 'failed';

export interface ScheduledNotification {
  identifier: string;
  created_at: string;
  scheduled_at: string;
  topic: string;
  topic_name: string;
  title: string;
  body: string;
  status: ScheduledNotificationStatus;
}

export interface ScheduledNotificationInput {
  topic: string;
  title: string;
  body: string;
  scheduled_at: string;
}

export const fetchScheduledNotifications = async (
  sessionToken: string,
  festivalId: string
): Promise<ScheduledNotification[]> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/notification/scheduled`, {
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || t().fetchScheduledFailed);
  }
  return data.data.scheduled_notifications;
};

export const createScheduledNotification = async (
  sessionToken: string,
  festivalId: string,
  notification: ScheduledNotificationInput
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/notification/scheduled`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify(notification),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().scheduleFailed);
  }
};

export const updateScheduledNotification = async (
  sessionToken: string,
  festivalId: string,
  identifier: string,
  notification: ScheduledNotificationInput
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/notification/scheduled/${identifier}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
    body: JSON.stringify(notification),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().updateScheduledFailed);
  }
};

export const cancelScheduledNotification = async (
  sessionToken: string,
  festivalId: string,
  identifier: string
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/notification/scheduled/${identifier}`, {
    method: 'DELETE',
    headers: {
      'X-Session-Token': sessionToken,
      'X-Festival-Id': festivalId,
    },
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || t().cancelScheduledFailed);
  }
};
